import { LoginRedirectTargetEnum, ModuleEnabledEnum, PermissionsEnum } from '../../graphql/generated';

/**
 * Maps LoginRedirectTargetEnum to frontend routes.
 * Paths include /frontend prefix because callers use window.location.href
 * which bypasses Next.js basePath.
 */
export const redirectRoutes: Record<LoginRedirectTargetEnum, string> = {
  [LoginRedirectTargetEnum.Dashboard]: '/frontend/dashboard/',
  [LoginRedirectTargetEnum.ChangeStructure]: '/frontend/change-structure/',
  [LoginRedirectTargetEnum.WorkInProgress]: '/frontend/wp/',
  [LoginRedirectTargetEnum.Calendar]: '/frontend/calendar/',
  [LoginRedirectTargetEnum.PatientPortal]: '/frontend/patient/',
  [LoginRedirectTargetEnum.Profile]: '/frontend/profile/',
};

export function resolveRedirectRoute(target?: string, fallback = '/frontend/profile/'): string {
  return redirectRoutes[target as LoginRedirectTargetEnum] ?? fallback;
}

/**
 * Contesto richiesto per decidere la rotta post-cambio-struttura.
 * Le funzioni provengono da `useModules()` / `usePermissions()`.
 */
export interface PostChangeStructureContext {
  isModuleEnabledByEnum: (moduleEnum: ModuleEnabledEnum) => boolean;
  hasPermissionForAnyRole: (permission: PermissionsEnum) => boolean;
}

/**
 * Risolve la rotta su cui atterrare dopo un cambio struttura riuscito.
 *
 * Source of truth FE per il post-change-structure. Qualunque utente che
 * abbia accesso al cruscotto (personale o supervisore) atterra su
 * `/frontend/dashboard/`, dove i tab visibili dipendono dai propri permessi
 * (vedi `pages/dashboard/index.tsx`). Tutti gli altri vanno alla home.
 *
 * Convenzione path: include `/frontend/` perché usato con `window.location`,
 * che bypassa il basePath di Next.js (vedi `redirectRoutes`).
 */
export function resolvePostChangeStructureRoute(ctx: PostChangeStructureContext): string {
  const hasSupervisorDashboard =
    ctx.isModuleEnabledByEnum(ModuleEnabledEnum.SupervisorDashboard) &&
    ctx.hasPermissionForAnyRole(PermissionsEnum.ViewSupervisorDashboard);

  const hasPersonalDashboard =
    ctx.isModuleEnabledByEnum(ModuleEnabledEnum.Dashboard) &&
    ctx.hasPermissionForAnyRole(PermissionsEnum.ViewMyDashboard);

  if (hasSupervisorDashboard || hasPersonalDashboard) {
    return redirectRoutes[LoginRedirectTargetEnum.Dashboard];
  }

  return '/frontend/';
}
